import { useFeatureIsOn } from '@frontend/react/hooks/growthbook/useFeatureIsOn';
import { useIsGrowthbookReady } from '@frontend/react/hooks/growthbook/useIsGrowthbookReady';
// Core always needs to be first
import { useAuthSlice, useTrackingSlice } from '@frontend/redux';
import { AnalyticsProvider } from '@frontend/web-react/components/Analytics/AnalyticsProvider';
import { LoadingSpinnerPanel } from '@frontend/web-react/components/Primitives/Loading/LoadingSpinnerPanel';
import { SSOProvider, useSSOLoadState } from '@frontend/web-react/components/SSO/SSOProvider';
import {
  useLazyRetrieveClientQuery,
  useLazyRetrieveClientStatusQuery,
  useRetrieveClientQuery,
  useRetrieveClientStatusQuery,
} from '@frontend/web/hooks/authApi';
import { useAppIdentity } from '@frontend/web/hooks/useAppIdentity';
import { useAuthentication } from '@frontend/web/hooks/useAuthentication';
import { mantineTheme } from '@frontend/web/mantine';
import { persistor, store } from '@frontend/web/redux/store';
import { router } from '@frontend/web/router';
import { Env } from '@frontend/web/utils/env';
import { isMembershipComplete, isProfileComplete } from '@frontend/web/utils/routing/redirectPolicy';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { RouterProvider, type AnyRouter } from '@tanstack/react-router';
import '@mantine/carousel/styles.layer.css';
import '@mantine/core/styles.layer.css';
import '@mantine/notifications/styles.layer.css';
import { lazy, Suspense, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { initApp } from './utils/initApp';
import { logLoadPerf } from './utils/logLoadPerf';
import { releaseMarketingScripts } from './utils/releaseMarketingScripts';

const { growthbook } = initApp();

type RouterProps = {
  routerInstance?: AnyRouter;
};

type AppProps = RouterProps & {
  analyticsMaxWaitMs?: number;
};

const isAgentationEnabled = Env.VITE_ENV === 'development' || Env.VITE_ENV === 'local';

const handleAnalyticsRelease = () => releaseMarketingScripts();

const Agentation = isAgentationEnabled
  ? lazy(() =>
      import('agentation').then((res) => ({
        default: res.Agentation,
      })),
    )
  : () => null;

// Needs to be wrapped inside Redux Provider/PersistGate before useAuthentication is accessible
export const Router = ({ routerInstance = router }: RouterProps = {}) => {
  const authentication = useAuthentication();
  const { ready: isGrowthbookReady } = useIsGrowthbookReady();
  const { enabled: isSignupChangesEnabled } = useFeatureIsOn('is_signup_changes_enabled');
  const {
    data: client,
    isFetching: isClientFetching,
    isLoading: isClientLoading,
  } = useRetrieveClientQuery(undefined, {
    skip: !authentication.isAuthenticated,
  });
  const {
    data: clientStatus,
    isFetching: isClientStatusFetching,
    isLoading: isClientStatusLoading,
  } = useRetrieveClientStatusQuery(undefined, {
    skip: !authentication.isAuthenticated,
  });

  const redirectContext = {
    isClientLoading: authentication.isAuthenticated ? isClientLoading || isClientFetching : false,
    isClientStatusLoading: authentication.isAuthenticated ? isClientStatusLoading || isClientStatusFetching : false,
    isGrowthbookReady,
    isSignupChangesEnabled,
    membershipComplete: isMembershipComplete({ client, clientStatus }),
    onboardingComplete: Boolean(clientStatus?.onboardingComplete),
    profileComplete: isProfileComplete(client),
  };

  return (
    <Suspense fallback={<div />}>
      <RouterProvider context={{ authentication, redirectContext }} router={routerInstance} />
    </Suspense>
  );
};

export const App = ({ analyticsMaxWaitMs, routerInstance }: AppProps = {}) => {
  const ssoLoadState = useSSOLoadState();
  const [retrieveClient, { isLoading: isClientLoading }] = useLazyRetrieveClientQuery();
  const [retrieveClientStatus] = useLazyRetrieveClientStatusQuery();
  const { client: reduxClient, storeClient, token } = useAuthSlice();
  const { fbclid, gclid } = useTrackingSlice();
  const { isIdentifyDone } = useAppIdentity({
    env: Env.VITE_ENV,
    fbclid,
    gclid,
    growthbook,
    isSsoLoading: ssoLoadState.status === 'loading',
    reduxClient,
    retrieveClient,
    retrieveClientStatus,
    storeClient,
    token,
  });

  useEffect(() => {
    if (ssoLoadState.status !== 'loading') {
      logLoadPerf(`Clerk bundle finished (${ssoLoadState.status})`);
    }
  }, [ssoLoadState.status]);

  // We want to wait until the client is identified with Growthbook, if they're logged in
  const isAppReadyToRender = !isClientLoading && (!token || isIdentifyDone);

  return (
    <AnalyticsProvider
      maxWaitMs={analyticsMaxWaitMs}
      onRelease={handleAnalyticsRelease}
      releaseTrigger={ssoLoadState.status !== 'loading'}
    >
      {!isAppReadyToRender ? <LoadingSpinnerPanel className="h-screen w-full pb-xxxl" fullScreen /> : null}
      <Router routerInstance={routerInstance} />
    </AnalyticsProvider>
  );
};

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const rootElement = document.getElementById('root')!;
if (!rootElement?.innerHTML) {
  const { VITE_CLERK_PUBLISHABLE_KEY } = Env;

  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <>
      <MantineProvider theme={mantineTheme}>
        <div className="min-h-screen">
          <Notifications position="bottom-left" />
          <SSOProvider publishableKey={VITE_CLERK_PUBLISHABLE_KEY}>
            <Provider store={store}>
              <PersistGate loading={null} persistor={persistor}>
                <GrowthBookProvider growthbook={growthbook}>
                  <App />
                </GrowthBookProvider>
              </PersistGate>
            </Provider>
          </SSOProvider>
          {/* <TanStackRouterDevtools initialIsOpen={false} router={router} /> */}
        </div>
      </MantineProvider>
      <Suspense fallback={null}>
        <Agentation />
      </Suspense>
    </>,
  );
}
